/**
 * PreSet Panel Registry (jurisdiction-agnostic)
 *
 * PreSetDocGenView 가 task type 별 패널을 직접 import 하지 않고
 * 이 registry 에서 lookup 한다. 각 jurisdiction(my, sg) 의 패널들은
 * 자기 자신을 plug-in 으로 등록한다.
 */

import type { Component } from 'vue';
import type { PreSetSelection } from '../types/preset.types';

export interface PanelDef {
  /** 고유 ID (예: 'my-incorporation', 'sg-incorporation'). 중복 시 덮어쓰기. */
  id: string;
  /** selection 기반 매칭 함수 — true 반환 시 이 패널 사용. */
  match: (selection: PreSetSelection) => boolean;
  /** Vue 컴포넌트 (defineAsyncComponent 권장). */
  component: Component;
  /** 패널 메타 — DocGenView 가 활용 (예: docType 식별). */
  meta?: {
    /** 'INCORPORATION' 등 — store/upstream 에 노출용. */
    docType?: string;
    /** 다른 표시용 식별자 추가 가능. */
    [key: string]: unknown;
  };
  /** 매칭 우선순위 (높을수록 먼저). 기본 0. */
  priority?: number;
}

const panels: PanelDef[] = [];

export function registerPanel(def: PanelDef): void {
  if (!def?.id) {
    console.warn('[panelRegistry] def.id 누락 — 등록 스킵', def);
    return;
  }
  const idx = panels.findIndex((p) => p.id === def.id);
  if (idx >= 0) {
    panels[idx] = def;
    return;
  }
  panels.push(def);
}

/** selection 에 매칭되는 첫 패널 정의 반환 (priority 큰 순). */
export function getPanel(selection: PreSetSelection): PanelDef | null {
  if (!selection) return null;
  const sorted = [...panels].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return sorted.find((p) => {
    try {
      return p.match(selection);
    } catch (err) {
      console.warn(`[panelRegistry] match() error in ${p.id}:`, err);
      return false;
    }
  }) || null;
}

/** 등록된 모든 패널 ID. */
export function getRegisteredPanelIds(): string[] {
  return panels.map((p) => p.id);
}

/** 테스트용. */
export function _clearPanelRegistry(): void {
  panels.length = 0;
}
